import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

const CONTROL =
  'w-full rounded-md border border-line-strong bg-surface px-2.5 py-1.5 text-sm text-ink ' +
  'placeholder:text-ink-faint focus:border-accent focus:outline-none disabled:opacity-60';

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
  htmlFor,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <label className={cn('block', className)} htmlFor={htmlFor}>
      <span className="mb-1 flex items-baseline gap-1 text-xs font-medium text-ink-dim">
        {label}
        {required ? <span className="text-danger">*</span> : null}
      </span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-ink-faint">{hint}</span>
      ) : null}
    </label>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, type, inputMode, ...rest }, ref) {
    /*
     * Числовите полета отварят цифровата клавиатура на телефона сами по себе
     * си, но `decimal` дава точката и запетаята — цени и тегла се пишат с тях.
     */
    const mode = inputMode ?? (type === 'number' ? 'decimal' : undefined);
    return (
      <input
        ref={ref}
        type={type}
        {...(mode ? { inputMode: mode } : {})}
        className={cn(CONTROL, className)}
        {...rest}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 3, ...rest }, ref) {
  return (
    <textarea ref={ref} rows={rows} className={cn(CONTROL, 'resize-y', className)} {...rest} />
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Родният `<select>`. Изглежда по-скромно от нарисуван списък, но работи с
 * клавиатура, с екранен четец и на всеки браузър в офиса без нито ред код.
 */
export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { options: SelectOption[]; placeholder?: string }
>(function Select({ options, placeholder, className, ...rest }, ref) {
  return (
    <div className="relative">
      <select ref={ref} className={cn(CONTROL, 'appearance-none pr-8', className)} {...rest}>
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
    </div>
  );
});

/** Дата и час. Родният контрол се показва на български от самия браузър. */
export const DatePicker = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { withTime?: boolean }
>(function DatePicker({ withTime = true, className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      type={withTime ? 'datetime-local' : 'date'}
      className={cn(CONTROL, 'font-mono tabular', className)}
      {...rest}
    />
  );
});

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  const id = useId();
  return (
    /*
      `kmdx-check` не носи външен вид — тя е закачалката, по която стилът за
      пръст (`@media (pointer: coarse)`) вдига целия ред на 44 px. Самото
      квадратче остава 20 px; това, което расте, е редът около него и надписът,
      който е свързан с `htmlFor` и следователно също превключва отметката.
    */
    <div className={cn('kmdx-check flex items-center gap-2', className)}>
      <input
        id={id}
        type="checkbox"
        className="size-4 shrink-0 rounded border-line-strong bg-surface accent-accent"
        {...rest}
      />
      <label htmlFor={id} className="select-none text-sm text-ink-dim">
        {label}
      </label>
    </div>
  );
}
